import { Button, CloseIcon, Header, IconButton, Paragraph, TrashCanIcon, notification } from "@datagame/ui";
import { Dialog } from "@headlessui/react";
import { Trans, useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";

import { USER_DELETE_CURRENT } from "../../../../graphql/mutations/UserMutations";
import { isAdmin } from "../../../../utils/user/isAdmin";
import { UserInterface } from "../../../profile/ProfileView/ProfileView";
import { useUserContext } from "../../../../contexts/user/UserContext";

import styles from "./DeleteUserModal.module.scss";

interface DeleteUserModalProps {
  onClose: () => void;
  user?: UserInterface | null;
}

export const DeleteUserModal = ({ onClose, user }: DeleteUserModalProps) => {
  const { t } = useTranslation();
  const [deleteUser] = useMutation(USER_DELETE_CURRENT);
  const { signOut } = useUserContext();

  const handleDelete = () => {
    deleteUser({
      onError: (error) => {
        notification.danger({ title: error.message, description: "" });
      },
      onCompleted: () => {
        onClose();
        signOut();
        location.reload();
      },
    });
  };

  if (!user) return null;

  const renderContent = () => {
    if (isAdmin(user.role)) {
      return (
        <ul className={styles.list}>
          <li>
            <Trans i18nKey={t("profile:deleteModal.adminInfo1")} />
          </li>
          <li>
            <Trans i18nKey={t("profile:deleteModal.adminInfo2")} />
          </li>
          <li>
            <Trans i18nKey={t("profile:deleteModal.adminInfo3")} />
          </li>

          <li>
            <Trans i18nKey={t("profile:deleteModal.adminInfo4")} />
          </li>
        </ul>
      );
    }

    return (
      <ul className={styles.list}>
        <li>
          <Trans i18nKey={t("profile:deleteModal.userInfo1")} />
        </li>
        <li>
          <Trans i18nKey={t("profile:deleteModal.userInfo2")} />
        </li>
        <li>
          <Trans i18nKey={t("profile:deleteModal.userInfo3")} />
        </li>

        <li>
          <Trans i18nKey={t("profile:deleteModal.userInfo4")} />
        </li>
      </ul>
    );
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <div className={styles.overlay} />
      <Dialog.Panel>
        <div className={styles.modal}>
          <div className={styles.header}>
            <Header variant="h5">{t("profile:deleteModal.title")}</Header>

            <IconButton size="small" onClick={onClose} className={styles.close} icon={<CloseIcon />} />
          </div>

          <div>
            <Header variant="h6" className={styles.warning}>
              {t("profile:deleteModal.warning")}
            </Header>
            <Paragraph size="small" color="secondary">
              {renderContent()}
            </Paragraph>
          </div>

          <div className={styles.buttons}>
            <Button icon={<CloseIcon />} kind="outline" iconSide="left" size="small" onClick={onClose} color="black">
              {t("common:cancel")}
            </Button>
            <Button icon={<TrashCanIcon />} color="red" iconSide="left" size="small" onClick={handleDelete}>
              {t("common:delete")}
            </Button>
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};
